/**
 * Sending a project note to someone (0048).
 *
 * Notes stopped being published to the client portal — they are the designer's
 * own working notes, including the ones ABOUT the client — so anything a client
 * or a contractor should know is now SENT, deliberately, to a person.
 *
 * That is the whole change in one sentence: a note used to reach the client by
 * existing, and now it reaches them because the designer chose to send it.
 *
 * Pure, and composed at RENDER time: `navigator.share()` must be called with
 * the text already in hand (share-sheet.ts's gesture trap).
 */

import type { NoteRow, DesignerContactRow, ProjectRow } from '@/types/database';

export type NoteFields = Pick<NoteRow, 'title' | 'body'>;

export interface NoteMessageInput {
  note: NoteFields;
  projectName: string;
  /** Who it's going to, for the greeting. */
  toName?: string | null;
  /** The firm, so they know who it's from. Never a built-in name. */
  fromName?: string | null;
}

/**
 * "Hi Sarah — an update on Westlake: Tile arrives Friday. The porcelain …
 *  — Searle Studio"
 *
 * The note's TITLE is dropped when it is also the first words of the body,
 * which is how most short notes are written; repeating it reads as a stutter
 * in a text message even though it looks fine on the page.
 */
export function noteMessage(input: NoteMessageInput): string {
  const first = firstName(input.toName);
  const hello = first ? `Hi ${first} — ` : '';
  const from = (input.fromName ?? '').trim();
  const signoff = from ? `\n\n— ${from}` : '';
  return `${hello}an update on ${input.projectName}:\n\n${noteText(input.note)}${signoff}`;
}

/**
 * The note as one block: its title, then its body, with the title left out
 * when the body already opens with it.
 */
export function noteText(note: NoteFields): string {
  const title = (note.title ?? '').trim();
  const body = (note.body ?? '').trim();
  if (!title) return body;
  if (!body) return title;
  // Compared case-insensitively: "Tile delay" above "TILE DELAY — the …" is
  // the same stutter.
  if (body.toLowerCase().startsWith(title.toLowerCase())) return body;
  return `${title}\n${body}`;
}

/** The share sheet's title — what the OS shows above the app list. */
export function noteShareTitle(note: NoteFields, projectName: string): string {
  const title = (note.title ?? '').trim();
  return title ? `${projectName} — ${title}` : `${projectName} — an update`;
}

/** The fields choosing a recipient reads. */
export type NoteRecipient = Pick<DesignerContactRow,
  'id' | 'name' | 'company' | 'phone' | 'is_client' | 'is_contractor' | 'is_subcontractor' | 'is_vendor'>;

/**
 * Everyone a note can go to, clients first.
 *
 * Everyone on the project, not just clients: "may go to client, or contractor"
 * (Mick, 2026-09-30). Clients lead because a note is most often an update for
 * them, and a contractor is a deliberate second choice rather than a slip.
 *
 * A contact with no phone is still offered — the share sheet asks who to send
 * to, and an email-only contact is a perfectly good recipient there.
 */
export function noteRecipients<T extends NoteRecipient>(contacts: readonly T[]): T[] {
  const clients = contacts.filter((c) => c.is_client);
  const rest = contacts.filter((c) => !c.is_client);
  return [...clients, ...rest];
}

/** "Sarah Anderson — Anderson Residence (client)". */
export function recipientLabel(c: NoteRecipient): string {
  const who = c.company ? `${c.name} — ${c.company}` : c.name;
  const role = c.is_client ? 'client'
    : c.is_contractor ? 'contractor'
    : c.is_subcontractor ? 'subcontractor'
    : c.is_vendor ? 'vendor'
    : '';
  return role ? `${who} (${role})` : who;
}

/** The greeting name for a chosen recipient, or the project's client. */
export function greetingFor(
  contact: NoteRecipient | null,
  project: Pick<ProjectRow, 'client_name'>,
): string | null {
  const name = (contact?.name ?? '').trim();
  if (name) return name;
  // No contact chosen (the share sheet will ask): fall back to whatever the
  // project calls its client, which is where Mick's clients are recorded.
  return (project.client_name ?? '').trim() || null;
}

function firstName(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';
  // "The Andersons" is a household, not a person: "Hi The" is worse than no
  // greeting at all. Same rule as a change order's message.
  const first = trimmed.split(/\s+/)[0] ?? '';
  return /^(the|mr|mrs|ms|dr|mr\.|mrs\.|ms\.|dr\.)$/i.test(first) ? trimmed : first;
}
